import React from 'react';
import PropTypes from 'prop-types';

import {OEMediaViewerPlaybackState, OEMediaViewerReadyState, OEMediaViewerContentSizeMode} from '../oe-media-viewer-model';
import OEMediaViewerContentController from './oe-media-viewer-content-controller';
import OEColor from '../../color-picker/oe-color';
import OEToolbox from '../../../lib/tools/oe-toolbox';

export default class OEMediaViewerVideoContentController extends React.PureComponent {

    constructor(props) {
        super(props);

        this.state = {
            autoplay: true,  // we set autoplay attribute for video element to true since some browsers don't start caching otherwise, i.e., iOS safari
            readyState: OEMediaViewerReadyState.noMeta
        };

        this.canPlayState = 4;  // ready state at wich playback is possible

        this.ref = null;
        this.playbackState = OEMediaViewerPlaybackState.pause;
        this.currentTime = 0;
        this.duration = 0;
        this.readyState = OEMediaViewerReadyState.noMeta;
        this.contentSize = null;

        this.onRef = this.onRef.bind(this);

        this.onLoadedMetaData = this.onLoadedMetaData.bind(this);
        this.onWaiting = this.onWaiting.bind(this);
        this.onCanPlay = this.onCanPlay.bind(this);
        this.onError = this.onError.bind(this);
        this.onDurationChanged = this.onDurationChanged.bind(this);
        this.onTimeUpdate = this.onTimeUpdate.bind(this);
        this.onPlaybackStateChanged = this.onPlaybackStateChanged.bind(this);
    }

    getReadyState() {
        return this.readyState;
    }

    getContentSize()    {
        return this.contentSize;
    }

    play()  {
        this.setPlaybackState(OEMediaViewerPlaybackState.play);
    }

    pause()  {
        this.setPlaybackState(OEMediaViewerPlaybackState.pause);
    }

    stop()  {
        this.pause();
        this.seek(0);
    }

    getPlaybackState()  {
        return this.playbackState;
    }

    setPlaybackState(playbackState)  {
        if(this.ref && this.ref.readyState > 0 && !this.disablePlaybackStateListener) {
            if(playbackState === OEMediaViewerPlaybackState.pause)  {
                this.ref.pause();
            } else {
                this.ref.play().catch((error) => {
                    // just catch the promise error and do nothing,
                    // it is usually thrown when the browser does not allow autoplaying and the app tries to start playback outside of a user action handler
                    // we handle this case by doing nothing which results in the behavior that automatic playback just does not start & the user has to start playback with an user action
                });
            }
        } else {
            if(playbackState !== this.playbackState)  {
                this.playbackState = playbackState;
                this.props.onPlaybackStateChanged(this.playbackState);
            }
        }
    }

    getCurrentTime()    {
        return this.currentTime;
    }

    getDuration()    {
        return this.duration;
    }

    getProgress()   {
        return !this.ref && typeof(this.appliedProgress) === 'number' ? this.appliedProgress : (this.duration > 0 ? this.currentTime / this.duration : 0);
    }

    setProgress(progress)   {
        if(this.ref && this.ref.readyState > 0) {
            let time = progress * this.duration;
            this.seek(time);
        } else {
            this.appliedProgress = progress;
        }
    }

    seek(seconds)    {
        if(this.ref && this.ref.readyState > 0) {
            if(this.ref.currentTime !== seconds)    {
                this.ref.currentTime = seconds;
            }
        } else {
            if(seconds !== this.currentTime)  {
                this.currentTime = seconds;
                this.props.onCurrentTimeChanged(this.currentTime);
            }
        }
        this.appliedProgress = null;
    }

    updateStateFromRef()    {
        let oldReadyState = this.readyState;
        let oldPlaybackState = this.playbackState;
        let oldCurrentTime = this.currentTime;
        let oldDuration = this.duration;
        let oldContentSize = this.contentSize;

        if(!this.ref)   {
            this.readyState = OEMediaViewerReadyState.noMeta;
            this.playbackState = OEMediaViewerPlaybackState.pause;
            this.currentTime = 0;
            this.duration = 0;
        } else {

            if(this.ref.readyState > 0)   {
                if(typeof(this.appliedProgress) === 'number')    {
                    this.setProgress(this.appliedProgress);
                    this.appliedProgress = null;
                } else {
                    this.seek(this.currentTime);
                }
                this.setPlaybackState(this.playbackState);

                this.playbackState = this.ref.paused ? OEMediaViewerPlaybackState.pause : OEMediaViewerPlaybackState.play;
                this.currentTime = this.ref.currentTime;
                this.duration = this.ref.duration;
            }

            if(this.ref.readyState >= this.canPlayState && !this.ref.seeking)   {
                this.readyState = OEMediaViewerReadyState.ready;
            } else if(this.ref.readyState > 0) {
                this.readyState = OEMediaViewerReadyState.waiting;
            } else {
                this.readyState = OEMediaViewerReadyState.noMeta;
            }

            if(this.readyState > OEMediaViewerReadyState.noMeta)    {
                this.contentSize = {w: this.ref.videoWidth, h: this.ref.videoHeight};
            }
        }

        if(oldReadyState !== this.readyState)    {
            this.setState({readyState: this.readyState});
            this.props.onReadyStateChanged(this.readyState);
        }

        if(oldPlaybackState !== this.playbackState)    {
            this.props.onPlaybackStateChanged(this.playbackState);
        }

        if(oldCurrentTime !== this.currentTime)    {
            this.props.onCurrentTimeChanged(this.currentTime);
        }

        if(oldDuration !== this.duration)    {
            this.props.onDurationChanged(this.duration);
        }

        if(!OEToolbox.shallowEqual(this.contentSize, oldContentSize))    {
            this.props.onContentSizeChanged(this.contentSize);
        }
    }

    registerListener()  {
        if(!this.ref)   return;

        if(!this.eventListener) {
            this.eventListener = {};

            this.eventListener.onLoadedMetaData = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video loadedmetadata', e);
                this.onLoadedMetaData();
            }.bind(this);

            this.eventListener.onWaiting = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video waiting', e);
                this.onWaiting();
            }.bind(this);

            this.eventListener.onCanPlay = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video canplay', e);
                if(!this.ref.seeking)  this.onCanPlay();
            }.bind(this);

            this.eventListener.onSeeking = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video seeking', e);

                if(bowserTool.safari)   {
                    // second part of the workaround for safari, see seeked
                    this.disablePlaybackStateListener = true;
                    this.ref.pause();
                }

                this.onWaiting();
            }.bind(this);

            this.eventListener.onSeeked = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video seeked - ready state - ' + this.ref.readyState.toString(), e);

                if(bowserTool.safari)   {
                    // workaround for safari showing strange behaviour when video element is playing while seeking
                    this.disablePlaybackStateListener = false;
                    if(this.playbackState === OEMediaViewerPlaybackState.play)  {
                        this.ref.play();
                    }
                }

                if(bowserTool.msie || this.ref.readyState >= this.canPlayState)   {
                    // for ie a ready state check is not necessary & missleading because ready state at seekend for ie could be < 4
                    this.onCanPlay();
                }

            }.bind(this);

            this.eventListener.onDurationChanged = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video durationchange', e);
                this.onDurationChanged();
            }.bind(this);

            this.eventListener.onTimeUpdate = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video timeupdate', e);
                this.onTimeUpdate();
            }.bind(this);

            this.eventListener.onPlay = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video play', e);
                if(this.disablePlaybackStateListener) return;
                this.onPlaybackStateChanged();
            }.bind(this);

            this.eventListener.onPause = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video pause', e);
                if(this.disablePlaybackStateListener) return;
                this.onPlaybackStateChanged();
            }.bind(this);

            this.eventListener.onEnded = function(e) {
                if(this.ref !== e.target)  return;
                //console.log('Video ended', e);
                this.onEnded();
            }.bind(this);

            this.eventListener.onError = function(e) {
                if(this.ref !== e.target)  return;
                console.log('Video error', e);
                this.onError();
            }.bind(this);
        }

        this.ref.addEventListener('loadedmetadata', this.eventListener.onLoadedMetaData, false);
        this.ref.addEventListener('waiting', this.eventListener.onWaiting, false);
        this.ref.addEventListener('canplay', this.eventListener.onCanPlay, false);
        this.ref.addEventListener('seeking', this.eventListener.onSeeking, false);
        this.ref.addEventListener('seeked', this.eventListener.onSeeked, false);
        this.ref.addEventListener('durationchange', this.eventListener.onDurationChanged, false);
        this.ref.addEventListener('timeupdate', this.eventListener.onTimeUpdate, false);
        this.ref.addEventListener('play', this.eventListener.onPlay, false);
        this.ref.addEventListener('pause', this.eventListener.onPause, false);
        this.ref.addEventListener('ended', this.eventListener.onEnded, false);
        this.ref.addEventListener('error', this.eventListener.onError, false);
    }

    unregisterListener()    {
        if(!this.ref || !this.eventListener)   return;

        this.ref.removeEventListener('loadedmetadata', this.eventListener.onLoadedMetaData, false);
        this.ref.removeEventListener('waiting', this.eventListener.onWaiting, false);
        this.ref.removeEventListener('canplay', this.eventListener.onCanPlay, false);
        this.ref.removeEventListener('seeking', this.eventListener.onSeeking, false);
        this.ref.removeEventListener('seeked', this.eventListener.onSeeked, false);
        this.ref.removeEventListener('durationchange', this.eventListener.onDurationChanged, false);
        this.ref.removeEventListener('timeupdate', this.eventListener.onTimeUpdate, false);
        this.ref.removeEventListener('play', this.eventListener.onPlay, false);
        this.ref.removeEventListener('pause', this.eventListener.onPause, false);
        this.ref.removeEventListener('ended', this.eventListener.onEnded, false);
        this.ref.removeEventListener('error', this.eventListener.onError, false);
    }

    onRef(ref) {
        if(ref === this.ref)    return;
        this.unregisterListener();
        this.ref = ref;
        this.updateStateFromRef();
        this.registerListener();
    }

    componentDidUpdate(prevProps) {
        if(prevProps.src !== this.props.src)     {
            this.updateStateFromRef();
        }
    }

    render() {
        let style;

        if(this.props.contentRect)  {

            if(this.props.contentRect.mode === OEMediaViewerContentSizeMode.fill)   {
                style = {backgroundColor: OEColor.toDOMStr(this.props.backgroundColor), width: '100%', height: '100%'};
            } else {
                style = {
                    backgroundColor: this.props.size || this.state.readyState === OEMediaViewerReadyState.noMeta ? OEColor.toDOMStr(this.props.backgroundColor) : 'rgba(0,0,0,0)',
                    top: this.props.contentRect.top,
                    left: this.props.contentRect.left,
                    width: this.props.contentRect.w,
                    height: this.props.contentRect.h
                };
            }
        } else {
            style = { backgroundColor: 'rgba(0,0,0,0)' };
        }

        return (
            <OEMediaViewerContentController
                className={this.props.className}
            >
                <video
                    src={this.props.src}
                    ref={this.onRef}
                    autoPlay={this.state.autoplay || this.props.autoplay}
                    preload="auto"
                    loop={this.props.loop}
                    style={style}
                />
            </OEMediaViewerContentController>
        );
    }

    onLoadedMetaData()  {
        if(this.readyState === OEMediaViewerReadyState.noMeta) {
            this.readyState = OEMediaViewerReadyState.waiting;
            this.setState({readyState: this.readyState});
            this.props.onReadyStateChanged(this.readyState);
        }

        let oldPlaybackState = this.playbackState;
        let oldCurrentTime = this.currentTime;
        let oldContentSize = this.contentSize;

        if(this.ref)    {
            if(this.ref.readyState > 0)   {
                if(typeof(this.appliedProgress) === 'number')    {
                    this.setProgress(this.appliedProgress);
                    this.appliedProgress = null;
                } else {
                    this.seek(this.currentTime);
                }
                this.setPlaybackState(this.playbackState);

                this.playbackState = this.ref.paused ? OEMediaViewerPlaybackState.pause : OEMediaViewerPlaybackState.play;
                this.currentTime = this.ref.currentTime;
            }

            if(this.readyState > OEMediaViewerReadyState.noMeta)    {
                this.contentSize = {w: this.ref.videoWidth, h: this.ref.videoHeight};
            }
        }

        if(oldPlaybackState !== this.playbackState)    {
            this.props.onPlaybackStateChanged(this.playbackState);
        }

        if(oldCurrentTime !== this.currentTime)    {
            this.props.onCurrentTimeChanged(this.currentTime);
        }

        if(!OEToolbox.shallowEqual(this.contentSize, oldContentSize))    {
            this.props.onContentSizeChanged(this.contentSize);
        }
    }

    onWaiting() {
        if(this.readyState !== OEMediaViewerReadyState.waiting) {
            this.readyState = OEMediaViewerReadyState.waiting;
            this.setState({readyState: this.readyState});
            this.props.onReadyStateChanged(this.readyState);
        }
    }

    onCanPlay() {
        if(this.readyState !== OEMediaViewerReadyState.ready) {
            this.readyState = OEMediaViewerReadyState.ready;
            this.setState({readyState: this.readyState});
            this.props.onReadyStateChanged(this.readyState);
        }
    }

    onError()   {
        if(this.readyState !== OEMediaViewerReadyState.error) {
            this.readyState = OEMediaViewerReadyState.error;
            this.setState({readyState: this.readyState});
            this.props.onReadyStateChanged(this.readyState);
        }
    }

    onDurationChanged()  {
        if(!this.ref) return;
        let duration = this.ref.duration;

        if(duration !== this.duration)  {
            this.duration = duration;
            this.props.onDurationChanged(this.duration);
        }
    }

    onTimeUpdate()  {
        if(!this.ref) return;
        let currentTime = this.ref.currentTime;

        if(currentTime !== this.currentTime)  {
            this.currentTime = currentTime;
            this.props.onCurrentTimeChanged(this.currentTime);
        }
    }

    onPlaybackStateChanged()  {
        if(!this.ref) return;
        let playbackState = this.ref.paused ? OEMediaViewerPlaybackState.pause : OEMediaViewerPlaybackState.play;

        if(playbackState !== this.playbackState)  {
            this.playbackState = playbackState;
            this.props.onPlaybackStateChanged(this.playbackState);
        }
    }

    onEnded()   {
        this.props.onEnded();
    }
}

OEMediaViewerVideoContentController.defaultProps = {
    className: '',
    autoplay: false,
    loop: false,
    onReadyStateChanged: (state) => {},
    onDurationChanged: (duration) => {},
    onCurrentTimeChanged: (currentTime) => {},
    onPlaybackStateChanged: (state) => {},
    onEnded: () => {},
    onContentSizeChanged: (size) => {}
};

OEMediaViewerVideoContentController.propTypes = {
    className: PropTypes.string,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    onReadyStateChanged: PropTypes.func,
    onCurrentTimeChanged: PropTypes.func,
    onDurationChanged: PropTypes.func,
    onPlaybackStateChanged: PropTypes.func,
    onEnded: PropTypes.func,
    onContentSizeChanged: PropTypes.func
};